import React from 'react';

import {GeneralSearchResponse} from 'src/openapi/admin/models/GeneralSearchResponse';

import {ListboxGroup} from '@pb/kit-component-listbox';

import {GroupProps, LocalItemProps, SuggestionSection} from './typedef';


export const isNumberQuery = (criteria: string) => /^\d+$/.test(criteria);

export const filterSuggestionSections = (
    filterQuery: string,
    sections: SuggestionSection[],
): {section: SuggestionSection, filteredItems: LocalItemProps[]}[] => {
    const q = (filterQuery || '').toLowerCase();

    return sections
        .map(section => {
            const filteredItems = section.suggestionItems.filter(item => {
                const name = (item.name || '').toLowerCase();
                const id = item.id !== undefined && item.id !== null ? String(item.id).toLowerCase() : '';
                return q.length === 0 || name.includes(q) || id.includes(q);
            });

            if (filteredItems.length === 0) {
                return undefined;
            }

            return {section, filteredItems};
        })
        .filter((result): result is {section: SuggestionSection, filteredItems: LocalItemProps[]} => !!result);
};

export const getSectionSearchType = (sectionTitle: string): 'ACCOUNT' | 'USER' => {
    return sectionTitle === 'Accounts' ? 'ACCOUNT' : 'USER';
};


export const mapGeneralSearchResponse = (
    response?: GeneralSearchResponse | null,
): SuggestionSection[] => {
    if (!response) {
        return [];
    }

    const sections: SuggestionSection[] = [];

    if (Array.isArray(response.accounts) && response.accounts.length > 0) {
        sections.push({
            title: 'Accounts',
            suggestionItems: response.accounts.map(account => ({
                id: account.id,
                name: account.name,
                additionData: {searchType: 'ACCOUNT'},
            })),
        });
    }

    if (Array.isArray(response.users) && response.users.length > 0) {
        sections.push({
            title: 'Users',
            suggestionItems: response.users.map(user => ({
                id: user.id,
                name: user.name,
                additionData: {searchType: 'USER'},
            })),
        });
    }

    return sections;
};

export const filteringData = (
    filterQuery: string,
    data: (GroupProps | LocalItemProps)[],
    renderSuggestions: (item: LocalItemProps, index: number) => React.ReactNode,
): React.ReactNode[] => {
    const q = (filterQuery || '').toLowerCase();

    const isGroup = (item: GroupProps | LocalItemProps): item is GroupProps => {
        return !!(item && (item as GroupProps).title && (item as GroupProps).items);
    };

    const itemMatches = (el: LocalItemProps) => {
        const name = (el.name || '').toString().toLowerCase();
        const id = el.id !== undefined && el.id !== null ? String(el.id).toLowerCase() : '';
        return (q.length === 0) || name.includes(q) || id.includes(q);
    };

    return data
        .map(item => {
            if (isGroup(item)) {
                const filteredData = item.items.filter(el => itemMatches(el));

                if (filteredData.length) {
                    return {
                        title: item.title,
                        items: filteredData,
                    } as GroupProps;
                }

                return undefined;
            }

            return item as LocalItemProps;
        })
        .filter((child): child is (GroupProps | LocalItemProps) => {
            if (child) {
                if (isGroup(child)) {
                    return true;
                }
                const li = child as LocalItemProps;
                return !!(li.name && itemMatches(li));
            }
            return false;
        })
        .map((item, i) => {
            if (isGroup(item)) {
                const caption = item.title;
                return React.createElement(
                    ListboxGroup,
                    {caption, key: item.title},
                    ...filteringData(filterQuery, item.items, renderSuggestions),
                );
            }

            return renderSuggestions(item as LocalItemProps, i);
        });
};
